import { ArrowRightIcon, Button, ControlIcon, EmptyState, Grid } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildFindings, type NormalizedFinding } from '../../../utils/timetable/normalizedFinding';
import { priorityToSeverity, type SeverityLevel, severityToken } from '../../../utils/timetable/severity';
import { useTimetableStore } from '../TimetableProvider';
import { FindingsItem } from './FindingsItem';
import styles from './FindingsPanel.module.scss';
import { InvalidationBanner } from './InvalidationBanner';
import { ReadinessSummary } from './ReadinessSummary';

const SECTIONS: SeverityLevel[] = ['error', 'warning', 'info'];

type FindingsPanelProps = {
  className?: string | undefined;
};

export const FindingsPanel: FC<FindingsPanelProps> = observer(({ className }) => {
  const { t } = useTranslation();
  const store = useTimetableStore();
  const [collapsed, setCollapsed] = useState<Set<SeverityLevel>>(new Set());

  const mode = store.controlMode.isActive ? 'control' : store.timefoldAnalyzeMode.isActive ? 'timefold' : null;
  const control = mode === 'control' ? store.getControlModeResult() : null;
  const timefold = mode === 'timefold' ? store.timefoldParsedAnalysis : null;

  const findings = buildFindings(control, timefold, {
    isKnownCard: (uuid) => !!store.getCardByUuid(uuid),
    resourceLabel: (type, uuid) =>
      store.getNameForUuid(uuid, type === 'group' || type === 'division' ? 'divisionGroup' : type),
  });

  const byLevel: Record<SeverityLevel, NormalizedFinding[]> = { error: [], warning: [], info: [] };
  for (const finding of findings) byLevel[priorityToSeverity(finding.severity)].push(finding);

  const toggleSection = (level: SeverityLevel) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  return (
    <Grid className={classNames('has-scrollbar', className)} style={{ height: 'fit-content' }}>
      {mode && store.areFindingsStale(mode) && (
        <InvalidationBanner
          onRerun={() => void (mode === 'control' ? store.runControlCheck() : store.runTimefoldCheck())}
          isLoading={mode === 'control' ? store.controlMode.isLoading : store.timefoldAnalyzeMode.isLoading}
        />
      )}
      {findings.length > 0 && (
        <ReadinessSummary
          blockers={byLevel.error.length}
          warnings={byLevel.warning.length}
          hints={byLevel.info.length}
        />
      )}
      {findings.length === 0 ? (
        <EmptyState
          size='small'
          subtitle={t('findings.empty')}
          icon={<ControlIcon />}
          iconColor='var(--color-success)'
        />
      ) : (
        SECTIONS.filter((level) => byLevel[level].length > 0).map((level) => {
          const token = severityToken(level);
          const isOpen = !collapsed.has(level);
          return (
            <div key={level} className={styles.section}>
              <div className={styles['section-header']} onClick={() => toggleSection(level)}>
                <ArrowRightIcon
                  className={classNames(styles['section-indicator'], 'small', { [styles.open]: isOpen })}
                />
                <token.Icon className='small' color={token.color} />
                <span>{t(token.labelKey)}</span>
                <span className={styles['section-count']}>{byLevel[level].length}</span>
              </div>
              {isOpen &&
                byLevel[level].map((finding) => <FindingsItem key={finding.key} finding={finding} mode={mode} />)}
            </div>
          );
        })
      )}
      {mode === 'timefold' && store.timefoldParsedAnalysis && (
        <Button
          className={styles['full-analysis']}
          hierarchy='secondary'
          onClick={() => void store.setTimefoldAnalyzeModal({ isOpen: true })}
        >
          {t('pinboard.timefoldAnalyze.openFullAnalysis')}
        </Button>
      )}
    </Grid>
  );
});
